import { useGetPlacesList } from "@birdwatch/client";

import { toPlaceFilterOption } from "../filter-options/place-filter-option";
import type { FilterOptionsResult } from "../types";
import { useDebouncedSearch } from "./use-debounced-search";

const PAGE_SIZE = 20;

/**
 * Place picker options for filter fields — debounced server search + infinite
 * paging, with `selectedOptions` resolved by ids so chips label places that are
 * not on the current page. One home for every place picker; the `{ in }`
 * operator object is load-bearing: the SDK shorthand downgrades a single id
 * ("a") to `filters[ids]=a` (eq), which the in-only list endpoint ignores —
 * returning unfiltered places and a wrong chip label for one selected place.
 */
export function usePlaceFilterOptions(ctx?: {
  selectedValues?: string[];
}): FilterOptionsResult {
  const selectedValues = ctx?.selectedValues ?? [];
  const { query, onSearch } = useDebouncedSearch();

  const placesResult = useGetPlacesList()
    .search(query || undefined)
    .take(PAGE_SIZE)
    .getInfinite();

  const selectedResult = useGetPlacesList()
    .filters({ ids: { in: selectedValues.join(",") } })
    .take(100)
    .get({ enabled: selectedValues.length > 0 });

  const places = placesResult.data?.pages.flatMap((page) => page.data) ?? [];
  const selected = selectedValues.length ? (selectedResult.data ?? []) : [];

  return {
    options: places.map(toPlaceFilterOption),
    selectedOptions: selected.map(toPlaceFilterOption),
    isLoading: placesResult.isLoading,
    onSearch,
    hasMore: placesResult.hasNextPage,
    fetchMore: placesResult.fetchNextPage,
  };
}
